/*
  SMA — copy a coupon code to the clipboard.

  Delegated from the document, so codes that arrive later (theme editor
  re-renders, quick view, a section swapped in by Dawn) work without
  re-initialising anything.

  The async Clipboard API needs a secure context, which the Shopify storefront
  always is — but the theme editor runs the preview in an iframe where the
  permission can be refused, so there is a selection-based fallback.
*/
(function () {
  var RESET_DELAY = 2000;

  function legacyCopy(text) {
    var field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.top = '-1000px';
    field.style.opacity = '0';
    document.body.appendChild(field);

    var selection = document.getSelection();
    var previous = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

    field.select();

    var copied = false;
    try {
      copied = document.execCommand('copy');
    } catch (e) {
      copied = false;
    }

    document.body.removeChild(field);

    if (previous) {
      selection.removeAllRanges();
      selection.addRange(previous);
    }

    return copied;
  }

  function copy(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).catch(function () {
        if (!legacyCopy(text)) throw new Error('copy failed');
      });
    }
    return legacyCopy(text) ? Promise.resolve() : Promise.reject(new Error('copy failed'));
  }

  function confirm(button) {
    var textNode = button.querySelector('[data-sma-copy-text]');
    var copiedLabel = button.dataset.copiedLabel || 'Copied';
    var copyLabel = button.dataset.copyLabel || 'Copy';

    button.classList.add('is-copied');
    if (textNode) textNode.textContent = copiedLabel;

    /* Announce it for screen readers, which don't see the class change. */
    button.setAttribute('aria-live', 'polite');

    clearTimeout(button.smaCopyTimer);
    button.smaCopyTimer = setTimeout(function () {
      button.classList.remove('is-copied');
      if (textNode) textNode.textContent = copyLabel;
    }, RESET_DELAY);
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest ? event.target.closest('[data-sma-copy]') : null;
    if (!button) return;

    event.preventDefault();

    var code = button.dataset.smaCopy;
    if (!code) return;

    copy(code)
      .then(function () {
        confirm(button);
      })
      .catch(function () {
        /* Clipboard refused: select the code so the customer can copy manually. */
        var card = button.closest('[data-sma-offer]') || button;
        var value = card.querySelector('.sma-offers__code-value');
        if (!value || !window.getSelection || !document.createRange) return;
        var range = document.createRange();
        range.selectNodeContents(value);
        var selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      });
  });
})();

/*
  SMA — minimum order value on offers.

  Each offer with a minimum carries data-min-cents. When the selected variant
  costs less, the card gets .is-locked and its strip reads
  "Add item worth ₹X more to avail" (wording from the block's
  data-locked-template, with [amount] replaced).

  The price comes from the hidden [data-sma-price-cents] marker inside
  #price-<section id>. Dawn swaps that container's HTML on every variant
  change, so a MutationObserver on it is all the wiring needed — no reliance
  on pubsub load order. Liquid renders the first state, so nothing flashes.
*/
(function () {
  var OFFERS = '[data-sma-offers]';

  function formatMoney(cents, format) {
    var fmt = format || '₹{{amount_no_decimals}}';
    var value = Math.max(0, Math.round(Number(cents) || 0));

    function group(number, decimals, thousands, decimal) {
      var fixed = (number / 100).toFixed(decimals);
      var parts = fixed.split('.');
      var whole = parts[0];
      /* Same 3-digit grouping Liquid's money filter uses. */
      whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      var out = whole + (parts[1] ? decimal + parts[1] : '');
      /* Match money_without_trailing_zeros: ₹300.00 → ₹300. */
      return out.replace(new RegExp('\\' + decimal + '0+$'), '');
    }

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      switch (key) {
        case 'amount_no_decimals':
          return group(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return group(value, 2, "'", '.');
        default:
          return group(value, 2, ',', '.');
      }
    });
  }

  function priceRootFor(offers) {
    var info = offers.closest('product-info, [data-section]');
    var sectionId = info && info.dataset.section;
    return sectionId ? document.getElementById('price-' + sectionId) : null;
  }

  function currentCents(priceRoot) {
    var marker = priceRoot && priceRoot.querySelector('[data-sma-price-cents]');
    if (!marker) return null;
    var cents = parseInt(marker.getAttribute('data-sma-price-cents'), 10);
    return isNaN(cents) ? null : cents;
  }

  function update(offers) {
    var cents = currentCents(priceRootFor(offers));
    if (cents === null) return;

    var template = offers.dataset.lockedTemplate || 'Add item worth [amount] more to avail';
    var format = offers.dataset.moneyFormat;

    offers.querySelectorAll('[data-sma-offer][data-min-cents]').forEach(function (offer) {
      var min = parseInt(offer.dataset.minCents, 10) || 0;
      var short = min - cents;
      var locked = min > 0 && short > 0;
      var strip = offer.querySelector('[data-sma-offer-lock]');

      offer.classList.toggle('is-locked', locked);
      if (!strip) return;
      if (locked) {
        strip.textContent = template.replace('[amount]', formatMoney(short, format));
        strip.hidden = false;
      } else {
        strip.hidden = true;
      }
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll(OFFERS).forEach(function (offers) {
      update(offers);

      var priceRoot = priceRootFor(offers);
      if (!priceRoot || priceRoot.smaOffersObserved) return;
      priceRoot.smaOffersObserved = true;

      new MutationObserver(function () {
        document.querySelectorAll(OFFERS).forEach(update);
      }).observe(priceRoot, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* Theme editor replaces the section in place; pick the new markup up. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  document.addEventListener('shopify:block:select', function (event) {
    init(event.target.closest('.shopify-section') || document);
  });
})();
